import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Layout, Form, Input, Select, Button, Table, Row, Col, DatePicker, Breadcrumb, Card, Spin, Tag, Space, Modal } from 'antd';
import dayjs from 'dayjs';
import { MAINAPIURL, APPBACKGROUND_COLOR } from '../appsettings.js';
import Footerdiv from "../layout/footer.js";
import Headerdiv from "../layout/header.js";
import { AlertError, AlertSuccess } from '../usableclasses.js';

const { Content } = Layout;
const { Option } = Select;
const { RangePicker } = DatePicker;
const sortBy = [{
    id: 'CREATED_DATE',
    label: 'Created Date/Time'
},
{
    id: 'REFERANCE',
    label: 'Order No'
},
{
    id: 'FINANCE_YEAR',
    label: 'Financial Year'
},
{
    id: 'STATUS',
    label: 'Current Status'
},
{
    id: 'POINTS',
    label: 'Points Value'
},
]

const Orders = () => {
    const [form] = Form.useForm();

    const [financialYears, setFinancialYears] = useState([]);
    const [loadingFinancialYears, setLoadingFinancialYears] = useState(false);

    const [statusOptions, setStatusOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [cancelModalVisible, setCancelModalVisible] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [yearList, setYearList] = useState([]);

    const navigate = useNavigate();
    const TOKEN = localStorage.getItem("AUTHCODE");
    const ApiURL = MAINAPIURL;

    const [selectedOrderId, setSelectedOrderId] = useState(null); // State to store selected order ID for cancellation
    const [cancelReason, setCancelReason] = useState('');

    useEffect(() => {
        axios.get(`${ApiURL}/master/orderstatus/dropdown`, {
            headers: { SystemAccessToken: TOKEN }
        }).then(res => setStatusOptions(res.data || []))
            .catch(() => setStatusOptions([]));
    }, [ApiURL, TOKEN]);

    useEffect(() => {
        const fetchFinancialYears = async () => {
            setLoadingFinancialYears(true);
            try {
                const response = await axios.get(`${ApiURL}/master/financeyear/dropdown`, {
                    headers: { SystemAccessToken: TOKEN }
                });
                if (response.data && Array.isArray(response.data)) {
                    setFinancialYears(response.data);
                } else {

                    setFinancialYears([]);
                }
            } catch (error) {

                AlertError('Failed to load financial years');
                setFinancialYears([]);
            } finally {
                setLoadingFinancialYears(false);
            }
        };
        fetchFinancialYears();
    }, [ApiURL, TOKEN]);

    useEffect(() => {
        fetchOrders(); // Initial fetch using default page=1, pageSize=10

        // Populate yearList for the dropdown
        let i = 0;
        const currentdate = new Date();
        let currentyear = currentdate.getFullYear();
        var newYearArray = [];

        do {
            let incyear = currentyear - i;
            newYearArray.push(incyear.toString());
            i++;
        } while (i < 10);
        setYearList(newYearArray);
    }, []); // Runs once on mount


    const fetchOrders = async (filterValues = {}, pageNum = page, pSize = pageSize) => {
        setLoading(true);
        let orderDateStartFormatted = "";
        let orderDateEndFormatted = "";

        if (filterValues.dateRange && filterValues.dateRange.length === 2) {
            orderDateStartFormatted = dayjs.isDayjs(filterValues.dateRange[0])
                ? dayjs(filterValues.dateRange[0]).format("YYYY-MM-DD")
                : filterValues.dateRange[0] || "";
            orderDateEndFormatted = dayjs.isDayjs(filterValues.dateRange[1])
                ? dayjs(filterValues.dateRange[1]).format("YYYY-MM-DD")
                : filterValues.dateRange[1] || "";
        }

        const body = {
            invoiceRef: filterValues.invoiceRef || "",
            consultId: filterValues.consultId || "",
            finYear: filterValues.finYear || "",
            sysYear: filterValues.sysYear || "",
            sysMonth: filterValues.sysMonth || "",
            orderDateStart: orderDateStartFormatted,
            orderDateEnd: orderDateEndFormatted,
            currentStatus: filterValues.currentStatus || "",
            pageLength: pSize,
            pageNumber: pageNum,
            orderColumn: filterValues.orderColumn || "CREATED_DATE",
            orderBY: filterValues.orderBY || "DESC"
        };

        try {
            const { data } = await axios.post(`${ApiURL}/orders/filterorders`, body, {
                headers: { SystemAccessToken: TOKEN }
            });
            setTableData(data.dataList || []);
            setTotalCount(data.rowcount || 0);
        } catch (error) {
            // TODO: Implement user-friendly error handling, e.g., toast notification
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const onFinish = values => {
        setPage(1);
        fetchOrders(values, 1);
    };

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'SUBMITTED':
                return 'cyan';         // neutral but active
            case 'UNPAID':
                return 'orange';       // warning
            case 'PAYMENT FAILED':
                return 'volcano';      // error-like
            case 'PAID':
                return 'green';        // success
            case 'PROCESSING':
                return 'blue';         // info
            case 'ORDER READY':
                return 'purple';       // ready but not yet shipped
            case 'COMPLETED':
                return 'geekblue';     // in transit
            case 'CANCELLED':
            case 'CANCELED':           // support both spellings
                return 'red';          // danger
            default:
                return 'default';      // fallback
        }
    };


    const columns = [
        { title: 'Order No', dataIndex: 'orderReferance', key: 'orderReferance' },
        { title: 'Platform', dataIndex: 'platform', key: 'platform', render: (text) => text?.toUpperCase() || 'N/A' },
        {
            title: 'Status',
            dataIndex: 'currentStage',
            key: 'currentStage',
            render: (status) => <Tag color={getStatusColor(status)}>{status?.toUpperCase()}</Tag>
        },
        { title: 'Cost', dataIndex: 'totalCost', key: 'totalCost' },
        { title: 'Need to Pay', dataIndex: 'toBePaid', key: 'toBePaid' },
        { title: 'Order D/T', dataIndex: 'createDateTime', key: 'createDateTime' },
        { title: 'Consultant', dataIndex: 'consultionId', key: 'consultionId' },
        { title: 'Order By', dataIndex: 'orderBy', key: 'orderBy' },
        { title: 'Year', dataIndex: 'sysYear', key: 'sysYear' },
        { title: 'Month', dataIndex: 'sysMonth', key: 'sysMonth' },
        { title: 'Ship Method', dataIndex: 'shippingMethod', key: 'shippingMethod' },
        {
            title: 'Actions',
            render: (_, record) => (
                <Space>
                    <Button type="primary" size='small' onClick={() => navigate('/orders/view/' + record.orderId)}>
                        View
                    </Button>
                    {(record.currentStage?.toUpperCase() !== 'CANCELLED' && record.currentStage?.toUpperCase() !== 'CANCELED') && (
                        <Button
                            danger
                            size="small"
                            type="default"
                            variant='outlined'
                            onClick={() => {
                                setSelectedOrderId(record.orderId);
                                setCancelModalVisible(true);
                            }}
                        >
                            Cancel
                        </Button>
                    )}
                </Space>
            ),
        }
    ];


    const handleCancelOk = async () => {
        if (!cancelReason.trim()) {
            AlertError('Cancellation reason is required.');
            return;
        }

        setLoading(true);
        try {
            // Retrieve userId from local storage
            const userId = localStorage.getItem('USERID');

            const payload = {
                orderId: selectedOrderId.toString(),
                requestBy: userId,
                requestReason: cancelReason,
            };

            await axios.post(`${ApiURL}/orders/addordercancelrequest`, payload, {
                headers: { SystemAccessToken: TOKEN },
            });
            fetchOrders(form.getFieldsValue(), page, pageSize);
            AlertSuccess('Order cancellation request submitted successfully.');
            handleCancelCancel(); // Close the modal after successful submission
        } catch (error) {
            console.error('There was an error submitting the cancel request:', error);
            AlertError(error.response?.data?.message || 'Please try again later. If the problem persists, contact support.');
        } finally {
            setLoading(false);
            setCancelModalVisible(false);
            setCancelReason('');
            setSelectedOrderId(null);
        }
    };

    const handleCancelCancel = () => {
        setCancelModalVisible(false);
        setCancelReason('');
        setSelectedOrderId(null);
    };

    return (
        <Spin spinning={loading}>
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
                <Headerdiv />
                <Content style={{ padding: '10px' }}>
                    <Breadcrumb style={{ margin: "16px 0" }}>
                        <Breadcrumb.Item>MLM Orders</Breadcrumb.Item>
                    </Breadcrumb>
                    <Card>
                        <Form form={form} onFinish={onFinish} layout="vertical">
                            <Row gutter={16}>
                                <Col xs={24} sm={12} lg={4}>
                                    <Form.Item name="invoiceRef" label="Invoice Reference">
                                        <Input placeholder="Enter Invoice Reference" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} lg={4}>
                                    <Form.Item name="consultId" label="Consultant ID">
                                        <Input placeholder="Enter Consultant ID" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} lg={4}>
                                    <Form.Item name="finYear" label="Financial Year">
                                        <Select
                                            placeholder="Select Financial Year"
                                            allowClear
                                            loading={loadingFinancialYears}
                                        >
                                            {financialYears.map(year => (
                                                <Option key={year.id} value={year.name}>
                                                    {year.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} lg={4}>
                                    <Form.Item name="sysYear" label="Year">
                                        <Select placeholder="Select Year" allowClear>
                                            {yearList.map((yearinList, index) => (
                                                <Option
                                                    key={yearinList || index}
                                                    value={yearinList || ''}
                                                >
                                                    {yearinList || 'Unknown Year'}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} lg={4}>
                                    <Form.Item name="sysMonth" label="Month">
                                        <Select placeholder="Select Month" allowClear>
                                            <Option value="01">January</Option>
                                            <Option value="02">February</Option>
                                            <Option value="03">March</Option>
                                            <Option value="04">April</Option>
                                            <Option value="05">May</Option>
                                            <Option value="06">June</Option>
                                            <Option value="07">July</Option>
                                            <Option value="08">August</Option>
                                            <Option value="09">September</Option>
                                            <Option value="10">October</Option>
                                            <Option value="11">November</Option>
                                            <Option value="12">December</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} lg={4}>
                                    <Form.Item name="currentStatus" label="Status">
                                        <Select allowClear placeholder="Select Status">
                                            {statusOptions.map(status => (
                                                <Option key={status.id} value={status.id}>{status.label}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col xs={24} sm={12} lg={6}>
                                    <Form.Item name="dateRange" label="Date Range">
                                        <RangePicker style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={6} lg={6}>
                                    <Form.Item name="orderColumn" label="Sort By">
                                        <Select defaultValue="CREATED_DATE" options={
                                            sortBy.map(item => ({
                                                value: item.id,
                                                label: item.label
                                            }))
                                        } />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={6} lg={5}>
                                    <Form.Item name="orderBy" label="Sort Order">
                                        <Select placeholder="Select Order">
                                            <Option value="ASC">Ascending</Option>
                                            <Option value="DESC">Descending</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} lg={3} sm={12} md="auto">
                                    <br />
                                    <Button type="primary" htmlType="submit" style={{ width: '100%' }}>Filter</Button>
                                </Col>
                                <Col xs={24} lg={3} sm={12} md="auto">
                                    <br />
                                    <Button onClick={() => { form.resetFields(); fetchOrders(); }} style={{ width: '100%' }}>Clear</Button>
                                </Col>
                            </Row>
                        </Form>
                    </Card>

                    <Table
                        columns={columns}
                        dataSource={tableData}
                        rowKey="orderNo"
                        loading={loading}
                        pagination={{
                            current: page,
                            pageSize: pageSize,
                            total: totalCount,
                            onChange: (p, ps) => {
                                setPage(p);
                                setPageSize(ps);
                                fetchOrders(form.getFieldsValue(), p, ps); // Pass the new page (p) and page size (ps)
                            }
                        }}
                        scroll={{ x: 'max-content' }}
                        style={{ marginTop: 20 }}
                    />
                    <Modal
                        title="Request Order Cancellation"
                        visible={cancelModalVisible}
                        onOk={handleCancelOk}
                        onCancel={handleCancelCancel}
                        okText="Request"
                        cancelText="Cancel"
                        okButtonProps={{ disabled: !cancelReason.trim() }}
                    >
                        <p>Are you sure you want to cancel this order?</p>
                        <Input.TextArea
                            required
                            rows={4}
                            placeholder="Please provide a reason for cancellation"
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                        />
                        {/*<Button onClick={() => navigate('/orders/view/' + record.orderId)}>View</Button>*/}

                    </Modal>
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
}



export default Orders;
